import React, { useState, useEffect, useMemo } from 'react';
import { ApolloProvider } from '@apollo/client';
import { useAppContext } from '@frontend/context/AppContext';
import { useBulkApproveRejectOrderRequest, useGetVariantsByIds } from '@frontend/applications/ProductFulfillmentApp/hooks';
import { Sheet, SheetContent, SheetFooter, SheetHeader, SheetTitle } from '@frontend/shadcn/components/ui/sheet';
import { Button } from '@frontend/shadcn/components/ui/button';
import { Card, CardContent } from '@frontend/shadcn/components/ui/card';
import { Badge } from '@frontend/shadcn/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@frontend/shadcn/components/ui/avatar';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@frontend/shadcn/components/ui/table';
import { Checkbox } from '@frontend/shadcn/components/ui/checkbox';
import { Alert } from '@frontend/shadcn/components/ui/alert';
import { PenToSquareIcon } from '@revfluence/fresh-icons/regular/esm';
import { ChevronDown, CoinsIcon, InfoIcon, Pen, Trash, AlertTriangle, X, InboxIcon } from 'lucide-react';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast } from '@frontend/shadcn/hooks/use-toast';
import { Tooltip, TooltipContent, TooltipTrigger } from '@frontend/shadcn/components/ui/tooltip';
import { BulkApproveRejectOperation } from '@frontend/applications/ProductFulfillmentApp/types/globalTypes';
import { useGetCurrencyCode } from '@frontend/applications/ProductFulfillmentApp/hooks/useGetCurrencyCode';
import { getCurrencySymbol } from '@frontend/app/containers/Settings/ProductFulfillment/utils';
import SelectProductDialog from './SelectProductDialog';
import AlertPFABoost from './AlertPFABoost';
import AlertPFAReject from './AlertPFAReject';
import AlertDeleteProduct from './AlertDeleteProduct';
import { useAdvertiserShopifyScopeCheck } from '../../hooks/useAdvertiserShopifyScopeCheck';
import { useGetReviewOrderDetails } from '../../hooks/useGetReviewOrderDetails';
import { TMember } from '../MembersWizard/types';

interface LocalVariant {
  id: number;
  imgUrl?: string;
  productImgUrl?: string;
  title: string;
  price: string;
  inventory_quantity: number;
  product_id: number;
  product_title: string;
  sellableOnlineQuantity: number;
  __typename: string;
}

interface IProps {
  open: boolean;
  setIsPFAApprovalDrawerOpen: (open: boolean) => void;
  selectedMembers: readonly TMember[];
  offerId: number;
  refresh: () => void;
  pfaOrderIds: string[];
}

interface IPropsDrawer {
  open: boolean;
  setIsPFAApprovalDrawerOpen: (open: boolean) => void;
  selectedMembers: readonly TMember[];
  refresh: () => void;
  pfaOrderIds: string[];
  resourceId: number;
  currencySymbol: string;
}

// Inner component that uses the hook
const PFAApprovalDrawerContent = ({
  open,
  setIsPFAApprovalDrawerOpen,
  selectedMembers,
  refresh,
  pfaOrderIds,
  resourceId,
  currencySymbol,
}: IPropsDrawer) => {
  const [isAccordionOpen, setIsAccordionOpen] = useState<number>(-1);
  const [deletedVariantIds, setDeletedVariantIds] = useState<Set<number>>(new Set());
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [isRejectAlertOpen, setIsRejectAlertOpen] = useState(false);
  const [isDeleteAlertOpen, setIsDeleteAlertOpen] = useState(false);
  const [orderToDelete, setOrderToDelete] = useState<{ orderId: string; productTitle?: string } | null>(null);
  const [actionInProgress, setActionInProgress] = useState<'APPROVE' | 'REJECT' | null>(null);
  const [excludeOutOfStockMembers, setExcludeOutOfStockMembers] = useState(false);
  const [showOutOfStockAlert, setShowOutOfStockAlert] = useState(true);
  const [isSelectProductDialogOpen, setIsSelectProductDialogOpen] = useState(false);
  const [productToReplace, setProductToReplace] = useState<{ id: string; title: string } | null>(null);

  // Get data directly from hooks
  const {
    fetchReviewOrderDetails,
    isReviewOrderDetailsLoading,
    reviewOrderDetailsResp,
    reviewOrderDetailsError
  } = useGetReviewOrderDetails();

  // Prepare variant IDs for fetching
  const [variantIdsToFetch, setVariantIdsToFetch] = useState<number[]>([]);
  const [resourceIdForFetch, setResourceIdForFetch] = useState<number | null>(null);

  // Get variants data from API
  const {
    variants: variantsFromApi,
  } = useGetVariantsByIds({
    variables: {
      resourceId: resourceIdForFetch || 0,
      variantIds: variantIdsToFetch,
    },
    skip: !resourceIdForFetch || variantIdsToFetch.length === 0,
  });

  // Track our local changes
  const [replacedVariants, setReplacedVariants] = useState<Record<number, LocalVariant>>({});
  const [updatedOrderDetails, setUpdatedOrderDetails] = useState<any>(null);

  // Combine API data with our local changes
  const localVariantsData = useMemo(() => {
    if (!variantsFromApi) return [];
    return variantsFromApi.map((variant) =>
      replacedVariants[variant.id] || variant
    );
  }, [variantsFromApi, replacedVariants]);

  // Use either updated order details or API response
  const effectiveOrderDetails = updatedOrderDetails || reviewOrderDetailsResp;

  // Check if any non-deleted variant is out of stock
  const hasOutOfStockProducts = localVariantsData?.some((variant) => {
    // Skip if variant is deleted
    if (deletedVariantIds.has(variant.id)) {
      return false;
    }

    // For replaced products (from Shopify), use totalInventory
    if (replacedVariants[variant.id]) {
      const isOutOfStock = replacedVariants[variant.id].inventory_quantity <= 0;
      return isOutOfStock;
    }

    // For original variants, use sellableOnlineQuantity directly
    const isOutOfStock = variant.sellableOnlineQuantity <= 0;
    return isOutOfStock;
  }) || false;

  // Add new check for if all non-deleted variants are out of stock
  const allProductsOutOfStock = localVariantsData?.every((variant) => {
    // Skip if variant is deleted
    if (deletedVariantIds.has(variant.id)) {
      return true;
    }

    // For replaced products (from Shopify), use totalInventory
    if (replacedVariants[variant.id]) {
      const isOutOfStock = replacedVariants[variant.id].inventory_quantity <= 0;
      return isOutOfStock;
    }

    // For original variants, use sellableOnlineQuantity directly
    const isOutOfStock = variant.sellableOnlineQuantity <= 0;
    return isOutOfStock;
  }) || false;

  // Fetch variants when order details are loaded and resourceId is available
  useEffect(() => {
    if (effectiveOrderDetails && effectiveOrderDetails.length > 0 && resourceId) {
      const allVariantIds = effectiveOrderDetails.flatMap((orderDetails) =>
        (orderDetails?.variantIds || []).map((id) => {
          const numId = parseInt(id, 10);
          return isNaN(numId) ? null : numId;
        })
      ).filter((id): id is number => id !== null);

      const uniqueVariantIds: number[] = Array.from(new Set(allVariantIds));

      if (uniqueVariantIds.length > 0) {
        setVariantIdsToFetch(uniqueVariantIds);
        setResourceIdForFetch(resourceId);
      }
    }
  }, [effectiveOrderDetails, resourceId]);

  // Handle product replacement
  const handleReplaceProduct = (oldProduct: any, newProduct: any) => {
    const oldVariantId = parseInt(oldProduct.id, 10);

    // Extract variant ID from the GraphQL ID format
    const newVariantId = parseInt(
      newProduct.shopifyProductVariants[0].id.split('/').pop() || '0',
      10
    );

    // Create the new variant object
    const newVariant: LocalVariant = {
      id: newVariantId,
      product_id: parseInt(newProduct.id.split('/').pop() || '0', 10),
      product_title: newProduct.title,
      title: newProduct.shopifyProductVariants[0].title,
      price: newProduct.price,
      inventory_quantity: newProduct.totalInventory,
      sellableOnlineQuantity: newProduct.totalInventory, // Set this equal to totalInventory for replaced products
      imgUrl: newProduct.shopifyProductVariants[0].imageUrl || newProduct.imgUrl,
      productImgUrl: newProduct.imgUrl,
      __typename: 'ShopifyProductVariant'
    };

    // Update our replaced variants map
    setReplacedVariants((prev) => {
      const updated = {
        ...prev,
        [oldVariantId]: newVariant
      };
      return updated;
    });

    // Update order details if we have any
    if (effectiveOrderDetails) {
      const newOrderDetails = effectiveOrderDetails.map((orderDetail) => {
        const newOrderDetail = JSON.parse(JSON.stringify(orderDetail));

        // Update orders array
        if (newOrderDetail.orders) {
          newOrderDetail.orders = newOrderDetail.orders.map((order: any) => {
            if (order.creatorOrderRequest?.line_items) {
              order.creatorOrderRequest.line_items = order.creatorOrderRequest.line_items.map((item: any) => {
                if (parseInt(item.variant_id, 10) === oldVariantId) {
                  return {
                    ...item,
                    price: parseFloat(newProduct.price),
                    title: newProduct.title,
                    variant_id: newVariantId,
                    variant_title: newProduct.shopifyProductVariants[0].title,
                    quantity: item.quantity // Preserve the original quantity
                  };
                }
                return item;
              });
            }
            return order;
          });
        }

        // Update variantIds array
        if (newOrderDetail.variantIds) {
          newOrderDetail.variantIds = newOrderDetail.variantIds.map((id: string) =>
            (parseInt(id, 10) === oldVariantId ? newVariantId.toString() : id)
          );
        }

        // Update variantMembersData
        if (newOrderDetail.variantMembersData) {
          const oldVariantData = newOrderDetail.variantMembersData[oldVariantId];
          if (oldVariantData) {
            // Preserve the member data but update with new variant
            newOrderDetail.variantMembersData[newVariantId] = {
              ...oldVariantData,
              totalQuantity: oldVariantData.totalQuantity,
              members: oldVariantData.members.map((member) => ({
                ...member,
                quantity: member.quantity // Preserve member quantities
              }))
            };
            delete newOrderDetail.variantMembersData[oldVariantId];
          }
        }

        return newOrderDetail;
      });

      setUpdatedOrderDetails(newOrderDetails);
    }

    setShowOutOfStockAlert(false);
  };

  // Reset deleted variants and dialog states when drawer opens with new data
  useEffect(() => {
    if (open && pfaOrderIds?.length > 0) {
      setDeletedVariantIds(new Set());
      setIsAlertOpen(false);
      setIsRejectAlertOpen(false);
      setIsDeleteAlertOpen(false);
      setOrderToDelete(null);
      setActionInProgress(null);
      setIsAccordionOpen(-1);
    }
  }, [open, pfaOrderIds]);

  // Fetch data when pfaOrderIds change
  useEffect(() => {
    if (pfaOrderIds && pfaOrderIds.length > 0) {
      fetchReviewOrderDetails({
        variables: {
          input: {
            orderIds: pfaOrderIds,
          },
        },
      });
    }
  }, [pfaOrderIds, fetchReviewOrderDetails]);

  // Add bulk approve/reject hook
  const {
    bulkApproveRejectOrderRequest,
  } = useBulkApproveRejectOrderRequest();

  // Handle delete product - show confirmation dialog
  const handleDeleteProduct = (variantId: number, productTitle?: string) => {
    setOrderToDelete({ orderId: variantId.toString(), productTitle });
    setIsDeleteAlertOpen(true);
  };

  // Confirm delete product
  const confirmDeleteProduct = () => {
    if (orderToDelete) {
      const variantId = parseInt(orderToDelete.orderId, 10);
      setDeletedVariantIds((prev) => {
        const newSet = new Set(prev).add(variantId);
        return newSet;
      });
      setOrderToDelete(null);
    }
  };

  // Keep all orders but track deleted variants separately
  const activeOrderDetailsArray = effectiveOrderDetails || [];

    // Calculate total members and members with stock from active variants
  const calculateMemberCounts = () => {
    // Get all unique member IDs from non-deleted variants
    const uniqueMembers = new Set<number>();
    const membersWithStockSet = new Set<number>();

    activeOrderDetailsArray.forEach((orderDetails) => {
      Object.entries(orderDetails.variantMembersData || {}).forEach(([variantId, variantData]: [string, any]) => {
        // Skip if this variant is deleted
        if (deletedVariantIds.has(parseInt(variantId, 10))) {
          return;
        }

        // Add all members for this variant to unique members set
        variantData?.members?.forEach((member: any) => {
          uniqueMembers.add(member.memberId);

          // Check if variant has stock
          const variant = localVariantsData?.find((v) => v.id === parseInt(variantId, 10));
          if (variant && variant.inventory_quantity > 0) {
            membersWithStockSet.add(member.memberId);
          }
        });
      });
    });

    return {
      totalMembers: uniqueMembers.size,
      membersWithStock: membersWithStockSet.size
    };
  };

  const { membersWithStock, totalMembers } = calculateMemberCounts();

  // Handle approve/reject actions
  const handleApprove = () => {
    // Show the approval confirmation alert
    setIsAlertOpen(true);
  };

  const executeApprove = async () => {
    try {
      setActionInProgress('APPROVE');

      // Build orderInputs array from the active order details, excluding deleted variants and out-of-stock products if checkbox is checked
      const orderInputs = activeOrderDetailsArray.flatMap((orderDetails) =>
        orderDetails.orders?.map((order) => {
          if (order.orderRequestStatus !== 'PENDING') {
            return null;
          }
          // Filter out line items for deleted variants and out-of-stock products if checkbox is checked
          const filteredLineItems = order.creatorOrderRequest?.line_items?.filter((lineItem) => {
            // Skip if variant is deleted
            if (deletedVariantIds.has(parseInt(lineItem.variant_id, 10))) {
              return false;
            }

            // Skip if variant is out of stock and exclude checkbox is checked
            if (excludeOutOfStockMembers) {
              const variant = localVariantsData?.find((v) => v.id === parseInt(lineItem.variant_id, 10));
              if (variant) {
                const inStockQuantity = (variant.sellableOnlineQuantity || 0);
                if (inStockQuantity <= 0) {
                  return false;
                }
              }
            }

            return true;
          }).map((lineItem) => {
            // Find if this variant has been replaced
            const updatedVariant = localVariantsData?.find((v) =>
              v.id === parseInt(lineItem.variant_id, 10)
            );

            // If variant was found in localVariantsData, use its updated information
            if (updatedVariant) {
              return {
                ...lineItem,
                price: parseFloat(updatedVariant.price),
                title: updatedVariant.product_title,
                variant_id: updatedVariant.id,
                variant_title: updatedVariant.title
              };
            }

            return lineItem;
          }) || [];

          return {
            orderId: order.id,
            creatorOrderRequest: {
              ...order.creatorOrderRequest,
              line_items: filteredLineItems,
            },
          };
        }).filter((orderInput) =>
          // Only include orders that are not null and still have line items after filtering
          orderInput !== null &&
          orderInput.creatorOrderRequest.line_items &&
          orderInput.creatorOrderRequest.line_items.length > 0
        ) || []
      );

      if (orderInputs.length === 0) {
        toast({
          title: 'Error',
          description: 'No order data available for approval',
          variant: 'destructive',
        });
        setActionInProgress(null);
        return;
      }

      await bulkApproveRejectOrderRequest({
        variables: {
          params: {
            operation: BulkApproveRejectOperation.APPROVE,
            orderInputs,
          },
        },
      });

      // Show success toast
      toast({
        title: 'Success',
        description: 'Orders successfully approved',
        variant: 'success',
      });

      // Refresh data after successful approval
      refresh();
      // Close the drawer
      setIsPFAApprovalDrawerOpen(false);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to approve orders. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setActionInProgress(null);
    }
  };

  const handleReject = () => {
    // Show the rejection confirmation alert
    setIsRejectAlertOpen(true);
  };

  const executeReject = async () => {
    try {
      setActionInProgress('REJECT');

      // Build orderInputs array from the active order details, excluding deleted variants
      const orderInputs = activeOrderDetailsArray.flatMap((orderDetails) =>
        orderDetails.orders?.map((order) => {
          if (order.orderRequestStatus !== 'PENDING') {
            return null; // only pending orders can be rejected
          }
          // Filter out line items for deleted variants
          const filteredLineItems = order.creatorOrderRequest?.line_items?.filter((lineItem) =>
            !deletedVariantIds.has(parseInt(lineItem.variant_id, 10))
          ) || [];

          return {
            orderId: order.id,
            creatorOrderRequest: {
              ...order.creatorOrderRequest,
              line_items: filteredLineItems,
            },
          };
        }).filter((orderInput) =>
          // Only include orders that are not null and still have line items after filtering
          orderInput !== null &&
          orderInput.creatorOrderRequest.line_items &&
          orderInput.creatorOrderRequest.line_items.length > 0
        ) || []
      );

      if (orderInputs.length === 0) {
        toast({
          title: 'Error',
          description: 'No order data available for rejection',
          variant: 'destructive',
        });
        setActionInProgress(null);
        return;
      }

      await bulkApproveRejectOrderRequest({
        variables: {
          params: {
            operation: BulkApproveRejectOperation.REJECT,
            orderInputs,
          },
        },
      });

      // Show success toast
      toast({
        title: 'Success',
        description: 'Orders successfully rejected',
        variant: 'destructive',
      });

      // Refresh data after successful rejection
      refresh();
      // Close the drawer
      setIsPFAApprovalDrawerOpen(false);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to reject orders. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setActionInProgress(null);
    }
  };

  const onClose = () => {
    setIsPFAApprovalDrawerOpen(false);
  };

  const toggleAccordion = (productIndex: number) => {
    setIsAccordionOpen((prev) => (prev === productIndex ? -1 : productIndex));
  };

  // Process real data from API response using active (non-deleted) orders
  const orderDetailsArray = activeOrderDetailsArray;

  // Calculate totals excluding deleted variants
  let totalProducts = 0;
  let totalValue = 0;
  const allMembers: Array<{ memberName: string; quantity: number; memberId: number }> = [];

  orderDetailsArray.forEach((orderDetails) => {
    if (orderDetails?.variantMembersData) {
      Object.entries(orderDetails.variantMembersData).forEach(([variantId, variantData]: [string, any]) => {
        const variantIdNum = parseInt(variantId, 10);

        // Skip deleted variants
        if (deletedVariantIds.has(variantIdNum)) return;

        if (variantData?.members) {
          allMembers.push(...variantData.members);
          totalProducts += variantData.totalQuantity || 0;

                     // Calculate value for this variant
           const variantInfo = localVariantsData?.find((v) => v.id === variantIdNum);
           if (variantInfo && variantInfo.price) {
             totalValue += Number(variantInfo.price) * (variantData.totalQuantity || 0);
           }
        }
      });
    }
  });

  // Check if we have valid order data for API calls (excluding deleted variants)
  const hasValidOrderData = activeOrderDetailsArray.some((orderDetails) =>
    orderDetails.orders && orderDetails.orders.length > 0 &&
    orderDetails.variantIds?.some((variantId) => !deletedVariantIds.has(parseInt(variantId, 10)))
  );

  // Check if all variants have been deleted
  const allOrdersDeleted = localVariantsData?.every((variant) => deletedVariantIds.has(variant.id)) || false;

  const summaryCards = [
    {
      title: 'Total Products Ordered',
      value: totalProducts.toString(),
      borderColor: 'border-t-[#6c77de]',
    },
    {
      title: 'Total Value',
      value: `${currencySymbol}${totalValue.toLocaleString()}`, // Currency symbol now dynamic
      borderColor: 'border-t-[#55a2df]',
    },
    // {
    //   title: 'Estimated Total Cost',
    //   value: `$${estimatedCost.toLocaleString()}`,
    //   borderColor: 'border-t-[#5db8b3]',
    // },
  ];

  if (isReviewOrderDetailsLoading) {
    return (
      <Sheet open={open} onOpenChange={onClose}>
        <SheetContent
          side="right"
          className="flex flex-col h-full bg-grey-0 p-0 gap-0 w-[610px] !max-w-[610px] overflow-x-hidden"
        >
          <SheetHeader className="flex items-center gap-2 flex-row p-4">
            <SheetTitle className="!m-0 text-base font-medium">Approve Orders in Bulk</SheetTitle>
          </SheetHeader>
          <div className="border-b" />
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2" />
              <p className="text-sm text-gray-500">Loading order details...</p>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  if (reviewOrderDetailsError) {
    return (
      <Sheet open={open} onOpenChange={onClose}>
        <SheetContent
          side="right"
          className="flex flex-col h-full bg-grey-0 p-0 gap-0 w-[610px] !max-w-[610px] overflow-x-hidden"
        >
          <SheetHeader className="flex items-center gap-2 flex-row p-4">
            <SheetTitle className="!m-0 text-base font-medium">Approve Orders in Bulk</SheetTitle>
          </SheetHeader>
          <div className="border-b" />
          <div className="flex items-center justify-center h-full">
            <div className="flex flex-col items-center justify-center gap-4 p-8">
              <div className="rounded-full bg-blue-50 p-4">
                <InboxIcon className="h-8 w-8 text-blue-500" />
              </div>
              <div className="text-center">
                <h3 className="text-lg font-semibold text-grey-6 mb-2">No Orders to Approve</h3>
                <p className="text-sm text-grey-5">
                  There are currently no pending orders that require your approval. Check back later for new requests.
                </p>
              </div>
              <Button
                variant="outline"
                onClick={onClose}
                className="mt-2"
              >
                Close
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent
        side="right"
        className="flex flex-col h-full bg-grey-0 p-0 gap-0 w-[610px] !max-w-[610px] overflow-x-hidden"
      >
        <SheetHeader className="flex items-center gap-2 flex-row p-4">
          <SheetTitle className="!m-0 text-base font-medium">Approve Orders in Bulk</SheetTitle>
        </SheetHeader>
        <div className="border-b" />
        <div className="bg-grey-1 flex gap-2 py-2 px-4 border-b items-center">
          <div className="bg-grey-2 rounded-full w-6 h-6 flex items-center justify-center">
            <PenToSquareIcon className="h-3 w-3" />
          </div>
          <p className="text-sm font-medium text-primary m-0">{selectedMembers?.length} Members Selected</p>
        </div>

        {hasOutOfStockProducts && showOutOfStockAlert && (
          <Alert className="flex items-center gap-3 px-4 py-3 bg-[#f8e8b8] rounded-2xl border-none mx-4 mt-4 w-fit">
            <FontAwesomeIcon icon={faExclamationTriangle} className="text-[#be8e18]" />
            <div className="flex-1 font-regular-100-paragraph text-[#16282d] text-[14px] tracking-[0px] leading-[24px]">
              To approve the order, select an alternate for the Out of stock product
            </div>
            <button
              className="p-0 bg-transparent border-none cursor-pointer"
              onClick={() => setShowOutOfStockAlert(false)}
            >
              <X className="h-3.5 w-3.5 text-[#16282d]" />
            </button>
          </Alert>
        )}

        <div className="flex flex-col items-start gap-4 p-4">
          <header className="items-center gap-2 self-stretch w-full flex relative">
            <div className="inline-flex items-center gap-2.5 relative">
              <CoinsIcon className="w-4 h-4 text-[#16282d]" />
            </div>
            <h2 className="text-lg font-semibold text-[#16282d]">Order Summary</h2>
            <div className="inline-flex items-center gap-2.5 relative ml-auto">
              <Tooltip>
                <TooltipTrigger>
                  <InfoIcon className="w-4 h-4 text-[#a2adb9]" />
                </TooltipTrigger>
                <TooltipContent>
                  <p>Order summary details</p>
                </TooltipContent>
              </Tooltip>
            </div>
          </header>
          <div className="flex flex-wrap items-center gap-2 self-stretch w-full">
            {summaryCards.map((card, index) => (
              <Card key={index} className="w-[270px] rounded-xl overflow-hidden border border-solid border-[#e5e8eb]">
                <CardContent
                  className={`flex flex-col items-start p-3 bg-white rounded-xl overflow-hidden border-t-[3px] ${card.borderColor}`}
                >
                  <div className="relative w-full text-sm text-[#6b7280] font-normal">{card.title}</div>
                  <div className="relative w-fit text-2xl font-semibold text-[#16282d] mt-1">{card.value}</div>
                </CardContent>
              </Card>
    ))}
          </div>
        </div>
        {!allOrdersDeleted ? (
          <div className="p-4 space-y-4">
            {(localVariantsData || []).map((variant, variantIndex) => {
              // Skip variants that have been marked as deleted
              if (deletedVariantIds.has(variant.id)) {
                console.log('Skipping deleted variant:', variant.id, variant.product_title);
                return null;
              }

              // Get variant member data from reviewOrderDetails
              const variantMembersData = activeOrderDetailsArray.flatMap((orderDetails) =>
                orderDetails.variantMembersData?.[variant.id.toString()] || null
              ).filter(Boolean)[0]; // Get the first non-null result

              // Skip variants that have no member data
              if (!variantMembersData) return null;

              const totalQuantityForVariant = variantMembersData.totalQuantity || 0;
              const membersForVariant = variantMembersData.members || [];

              return (
                <Card key={variant.id} className="overflow-hidden border border-solid border-[#e5e8eb] rounded-2xl">
                  <div className="flex items-center gap-3 p-2">
                    <div className="flex flex-col items-center gap-2">
                      <img
                        className="w-[72px] h-[72px] object-cover"
                        alt="Product"
                        src={(variant as any).imgUrl || (variant as any).productImgUrl || 'https://storage.googleapis.com/aspireiq-widgets/assets/content_image_placeholder.png'}
                      />
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="icon"
                          className="w-8 h-8 p-0 rounded-lg border border-solid border-grey-2"
                          onClick={() => {
                            setProductToReplace({
                              id: variant.id.toString(),
                              title: variant.product_title
                            });
                            setIsSelectProductDialogOpen(true);
                          }}
                        >
                          <Pen className="w-3.5 h-3.5 text-[#16282d]" />
                        </Button>
                        <Button
                          variant="outline"
                          size="icon"
                          className="w-8 h-8 p-0 rounded-lg border border-solid border-grey-2"
                          onClick={() => {
                            // Delete this specific variant
                            handleDeleteProduct(variant.id, variant.product_title);
                          }}
                          title="Delete this product"
                        >
                          <Trash className="w-3.5 h-3.5 text-[#16282d]" />
                        </Button>
                      </div>
                    </div>
                    <CardContent className="flex flex-col w-full items-start gap-1 p-0">
                      <div className="flex items-start justify-between w-full">
                        <div className="flex-1 mt-[-1.00px] font-medium text-[#16282d] text-[14px] leading-[24px]">
                          {variant.product_title || variant.title || 'Unknown Product'}
                        </div>
                        {(() => {
                          const inStockQuantity = (variant.sellableOnlineQuantity || 0);
                          const totalRequested = activeOrderDetailsArray
                            .flatMap((orderDetails) =>
                              Object.entries(orderDetails.variantMembersData || {})
                                .filter(([variantId]) => variantId === variant.id.toString())
                                .map(([_, data]: [string, any]) => data?.totalQuantity || 0)
                            )
                            .reduce((sum, qty) => sum + qty, 0);

                          if (inStockQuantity <= 0) {
                            return (
                              <Badge
                                variant="secondary"
                                className="px-2 py-0.5 bg-[#e5e8eb] text-[#16282d] rounded-lg font-normal text-xs"
                              >
                                Out Of Stock
                              </Badge>
                            );
                          } else if (inStockQuantity < totalRequested) {
                            return (
                              <Badge
                                variant="outline"
                                className="flex items-center gap-1 px-2 py-0.5 bg-yellow-2 border-none rounded-lg h-auto"
                              >
                                <AlertTriangle className="h-3 w-3 text-yellow-6" size={12} />
                                <span className="font-regular-75-caption text-grey-6 text-xs tracking-[0] leading-5 whitespace-nowrap">
                                  In Stock - {inStockQuantity}
                                </span>
                              </Badge>
                            );
                          } else {
                            return (
                              <Badge className="px-2 py-0.5 bg-[#d1f6d0] text-[#16282d] font-normal text-[12px] rounded-lg">
                                In Stock - {inStockQuantity}
                              </Badge>
                            );
                          }
                        })()}
                      </div>
                      <div className="flex items-start gap-2 w-full">
                        <div className="flex-1 mt-[-1.00px] font-normal text-[#5d6974] text-[12px] leading-[20px]">
                          Total Quantity Requested
                        </div>
                        <div className="font-medium text-[#16282d] text-[12px] leading-[20px]">{totalQuantityForVariant}</div>
                      </div>
                      <div className="flex items-start gap-2 w-full">
                        <div className="flex-1 mt-[-1.00px] font-normal text-[#5d6974] text-[12px] leading-[20px]">
                          Product Cost
                        </div>
                        {/* Currency symbol now dynamic */}
                        <div className="font-medium text-[#16282d] text-[12px] leading-[20px]">{currencySymbol}{variant.price || 0}</div>
                      </div>
                      <div className="flex items-start gap-2 w-full">
                        <div className="flex-1 mt-[-1.00px] font-normal text-[#5d6974] text-[12px] leading-[20px]">
                          Selected By
                        </div>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => toggleAccordion(variantIndex)}
                            className="flex items-center gap-1 hover:bg-gray-50 p-1 rounded transition-colors"
                          >
                            <div className="font-medium text-grey-6 text-[12px] leading-[20px] underline">
                              {membersForVariant.length}/{selectedMembers?.length || 'Total'} Members
                            </div>
                            <ChevronDown
                              className={`w-4 h-4 text-grey-6 transition-transform duration-200 ${
                                isAccordionOpen === variantIndex ? 'rotate-180' : ''
                              }`}
                            />
                          </button>
                        </div>
                      </div>
                    </CardContent>
                  </div>
                  {isAccordionOpen === variantIndex && (
                    <div className="border-t border-solid border-[#e5e8eb]">
                      <Table className="border border-solid border-[#e5e8eb]">
                        <TableHeader>
                          <TableRow className="border-b border-solid border-[#e5e8eb]">
                            <TableHead className="h-10 bg-[#f8f9fa] font-medium text-[#5d6974] text-sm">Member</TableHead>
                            <TableHead className="h-10 bg-[#f8f9fa] font-medium text-[#5d6974] text-sm text-right">
                              <div className="flex items-center justify-end gap-2">
                                Quantity
                                <InfoIcon className="h-4 w-4 text-[#a2adb9]" />
                              </div>
                            </TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {membersForVariant.length > 0 ? (
                            membersForVariant.map((member, index) => (
                              <TableRow
                                key={`member-${member.memberId}-${index}`}
                                className="border-b border-solid border-[#e5e8eb] h-10"
                              >
                                <TableCell className="p-4 font-normal text-[#16282d] text-sm">
                                  <div className="flex items-center gap-2.5">
                                    <Avatar className="h-6 w-6 rounded-full">
                                      <AvatarImage src="" alt={member.memberName} className="object-cover" />
                                      <AvatarFallback>{member.memberName.charAt(0)}</AvatarFallback>
                                    </Avatar>
                                    {member.memberName}
                                  </div>
                                </TableCell>
                                <TableCell className="p-4 font-normal text-[#16282d] text-sm text-right">
                                  {member.quantity}
                                </TableCell>
                              </TableRow>
                            ))
                          ) : (
                            <TableRow className="border-b border-solid border-[#e5e8eb] h-10">
                              <TableCell colSpan={2} className="p-4 text-center text-sm text-gray-500">
                                No members found
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        ) : (
          <div className="flex items-center justify-center flex-1 p-8">
            <div className="text-center">
              <div className="mb-4">
                <Trash className="w-16 h-16 text-gray-300 mx-auto" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Order Found</h3>
              <p className="text-sm text-gray-500">
                You have no orders in queue. Please select at least one order to approve or reject.
              </p>
            </div>
          </div>
        )}
        <SheetFooter className="p-0 border-t border-gray-200 mt-auto">
          <div className="flex flex-col w-full">
            <div className="flex w-[576px] items-start gap-2 px-6 py-4 bg-grey-0 border-t border-grey-2">
              <Checkbox
                id="exclude-members"
                className="mt-1"
                checked={excludeOutOfStockMembers}
                onCheckedChange={(checked) => setExcludeOutOfStockMembers(checked as boolean)}
              />
              <label
                htmlFor="exclude-members"
                className="flex-1 font-medium-100 font-medium text-grey-6 text-[14px] leading-[24px]"
              >
                Exclude members with out of stock/low stock product requests
              </label>
            </div>
            <div className="flex items-center justify-start gap-3 w-full p-4">
              <Button
                variant="primary"
                onClick={handleApprove}
                disabled={
                  actionInProgress !== null ||
                  !hasValidOrderData ||
                  allOrdersDeleted ||
                  (hasOutOfStockProducts && !excludeOutOfStockMembers) ||
                  (excludeOutOfStockMembers && allProductsOutOfStock) // Add this condition
                }
              >
                {actionInProgress === 'APPROVE' ? 'Approving...' : 'Approve'}
              </Button>
              <Button
                variant="ghost"
                className="text-red-500 hover:text-red-600 hover:bg-red-50 font-medium"
                onClick={handleReject}
                disabled={actionInProgress !== null || !hasValidOrderData || allOrdersDeleted}
              >
                {actionInProgress === 'REJECT' ? 'Rejecting...' : 'Reject'}
              </Button>
            </div>
          </div>
        </SheetFooter>
      </SheetContent>
      <AlertPFABoost
        isOpen={isAlertOpen}
        onOpenChange={setIsAlertOpen}
        membersWithStock={membersWithStock}
        totalMembers={totalMembers}
        onConfirm={executeApprove}
      />
      <AlertPFAReject
        isOpen={isRejectAlertOpen}
        onOpenChange={setIsRejectAlertOpen}
        totalMembers={totalMembers}
        onConfirm={executeReject}
      />
      <AlertDeleteProduct
        isOpen={isDeleteAlertOpen}
        onOpenChange={setIsDeleteAlertOpen}
        onConfirm={confirmDeleteProduct}
        productTitle={orderToDelete?.productTitle}
      />
      <SelectProductDialog
        isOpen={isSelectProductDialogOpen}
        onClose={() => {
          setIsSelectProductDialogOpen(false);
          setProductToReplace(null);
        }}
        onProductSelect={(newProduct) => {
          if (productToReplace) {
            handleReplaceProduct(productToReplace, newProduct);
            toast({
              title: 'Success',
              description: 'Product replaced successfully',
              variant: 'success',
            });
          }
        }}
        currentProductTitle={productToReplace?.title || ''}
        currencySymbol={currencySymbol}
      />
    </Sheet>
  );
};

// Main component with Apollo Provider wrapper
const PFAApprovalDrawer = ({
  open,
  setIsPFAApprovalDrawerOpen,
  selectedMembers,
  offerId,
  refresh,
  pfaOrderIds,
}: IProps) => {
  const { pfaApolloClient } = useAppContext();

  const { currencyCode } = useGetCurrencyCode({
    fetchPolicy: 'cache-first',
    errorPolicy: 'ignore',
    client: pfaApolloClient,
  });

  const currencySymbol = getCurrencySymbol(currencyCode || 'USD');

  // Use STA Apollo client for advertiser Shopify scope check (outside PFA provider)
  const advertiserShopifyScopeCheck = useAdvertiserShopifyScopeCheck({
    skip: !offerId, // Skip if offer is not loaded
  });

  // Extract resourceId from advertiser Shopify scope check
  const resourceId = (advertiserShopifyScopeCheck?.data?.advertiser?.shopifyResources?.[0] as any)?.id;

  return (
    <ApolloProvider client={pfaApolloClient}>
      <PFAApprovalDrawerContent
        open={open}
        setIsPFAApprovalDrawerOpen={setIsPFAApprovalDrawerOpen}
        selectedMembers={selectedMembers}
        refresh={refresh}
        pfaOrderIds={pfaOrderIds}
        resourceId={resourceId}
        currencySymbol={currencySymbol}
      />
    </ApolloProvider>
  );
};

export default PFAApprovalDrawer;
